import React, { useRef, useEffect, useState } from 'react';

import { keyBy } from 'lodash';
import { stakesMargin as baseMargin, circlePlacementDx, circlePlacementDy, circleSpacer, textDx, textDy } from '../RiskScatterFullChart.functions';
import { getStakeholderScoreForScenarioDetailsColor } from 'utils/ranking';

const isSelected = (pointsList: string[], pointId) => {
    if (!pointsList) return false;
    return pointsList.find(p => p.id == pointId);
}

const sizes = {
    "0": 1,
    "low": 3,
    "medium": 5,
    "high": 7
}

const adaptScore = (stakeholder) => {
    const score = getStakeholderScoreForScenarioDetailsColor(stakeholder);

    if (score === null) return null

    return parseInt(score.toFixed(0))
}

const getAllInitials = (name: string) => {
    const clearedName = name ? name.replace(/[^a-zA-Z0-9]/g, ' ').replace(/(\s)+/, ' ') : '';
    return clearedName.split(' ').slice(0, 2).map((n) => (n[0] ? n[0].toUpperCase() : '') + (n[1] ? n[1] : '') + (n[2] ? n[2] : '')).join('')
}

const StakeholdersPointsCloudInner = (props: any) => {
    // Track previous data IDs to detect newly appearing elements
    const prevDataIdsRef = useRef<Set<string> | null>(null);
    const prevDataRef = useRef<Map<string, any>>(new Map());
    const [appearingIds, setAppearingIds] = useState<Set<string>>(new Set());
    const appearingTimerRef = useRef<NodeJS.Timeout | null>(null);

    const hasSomeElementsSelected = props.isSelectedMode || props.selectedPoints.length > 0

    const { data } = props;

    useEffect(() => {
        const currentIds = new Set(data.map((d: any) => d.id));
        const prevIds = prevDataIdsRef.current;

        // Skip animation on first render - just initialize the ref
        if (prevIds === null) {
            prevDataIdsRef.current = currentIds;
            // Store initial data for potential future disappearing animations
            const dataMap = new Map();
            data.forEach((d: any) => dataMap.set(d.id, d));
            prevDataRef.current = dataMap;
            return;
        }

        // Find new IDs that weren't in the previous render
        const newAppearingIds = new Set<string>();
        currentIds.forEach(id => {
            if (!prevIds.has(id)) {
                newAppearingIds.add(id);
            }
        });

        // Update previous IDs ref and data map
        prevDataIdsRef.current = currentIds;
        const dataMap = new Map();
        data.forEach((d: any) => dataMap.set(d.id, d));
        prevDataRef.current = dataMap;

        // Handle appearing items - clear previous timer first
        if (appearingTimerRef.current) {
            clearTimeout(appearingTimerRef.current);
        }
        
        if (newAppearingIds.size > 0) {
            setAppearingIds(newAppearingIds);
            appearingTimerRef.current = setTimeout(() => {
                setAppearingIds(new Set());
            }, 1500);
        }

        return () => {
            if (appearingTimerRef.current) {
                clearTimeout(appearingTimerRef.current);
            }
        };
    }, [data]);

    const isAppearing = (id: string): boolean => {
        return appearingIds.has(id);
    }

    // Use similarItems from parent - required for proper link coordination
    const similarStakeholders = props.similarItems;

    const margin = { top: 30, right: 30, bottom: 40, left: 40 },
        width = props.width - margin.left - margin.right

    return (

        <g className={`wrapper ${hasSomeElementsSelected ? "selectionMode" : ""}`} >

            {
                data.map((d, index) => {
                    // Skip rendering if element doesn't exist in similarStakeholders (needed for proper link coordination)
                    if (!similarStakeholders || !similarStakeholders[d.id]) return null;

                    // Must use similarStakeholders[d.id] to get the enriched object with correct index
                    const similarItem = similarStakeholders[d.id];

                    const score = adaptScore(d);
                    const originalScore = adaptScore(d.o || d);
                    const isWorse = score < originalScore && score !== null
                    const isBetter = score > originalScore && score !== null

                    const selected = isSelected(props.selectedPoints, d.id)

                    // Calculate offset for similar items - same formula as in stakeholdersBandXY
                    const offsetX = circleSpacer(similarItem.similarCount) * (props.width * baseMargin) * circlePlacementDx(similarItem, similarStakeholders);
                    const offsetY = circleSpacer(similarItem.similarCount) * (props.width * baseMargin) * circlePlacementDy(similarItem, similarStakeholders);

                    return <g key={d.id}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (props.onClick) props.onClick(d)
                        }}
                        onMouseOver={() => {
                            if (props.onHover) props.onHover(d)
                        }}
                        onMouseOut={() => {
                            if (props.onHover) props.onHover(null)
                        }}
                        style={{
                            // Include offset in transform to match link position calculations exactly
                            transform: `translate(${props.x(score) + offsetX}px, ${offsetY}px)`,
                            transition: "transform 1.8s ease-in-out"
                        }}
                        className={`stakeholder ${selected ? "selected" : ""} ${isAppearing(d.id) ? "appearing" : ""}`}
                    >
                        <foreignObject className="aura" width={200} height={200}>
                            <div></div>
                        </foreignObject>
                        <circle
                            className="risk-circle"
                            cx={0}
                            cy={0}
                            r={sizes[(d.impact || d.o?.impact)]}
                            fill={selected ? "orange" : isWorse ? "#E70549" :
                                isBetter ? "#24CCB8" :
                                    "#000000"
                            }
                            strokeWidth={selected ? "4" : isWorse ? "6" :
                                isBetter ? "6" :
                                    "6"
                            }
                            stroke={selected ? "orange" : isWorse && d.isOpportunity ? "#00000044" : isWorse && !d.isOpportunity ? "#FFFFFF55" :
                                isBetter && d.isOpportunity ? "#FFFFFF55" : isBetter && !d.isOpportunity ? "#00000044" :
                                    "#00000001"
                            }

                            style={{
                                transition: selected ? "fill 1.8s ease-in-out" : "fill 1.8s ease-in-out, stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out"
                            }}
                        >

                        </circle>

                        <circle
                            className="risk-circle"
                            cx={0}
                            cy={0}
                            r={sizes[(d.impact || d.o?.impact)]}
                            fill={isWorse ? "#E70549" :
                                isBetter ? "#24CCB8" :
                                    "#000000"
                            }
                            strokeWidth={isWorse ? "1" :
                                isBetter ? "1" :
                                    "6"
                            }
                            stroke={isWorse ? "#FFFFFF" :
                                isBetter ? "#FFFFFF" :
                                    "#00000001"
                            }
                            style={{
                                transition: selected ? "stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out" : "fill 1.8s ease-in-out, stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out"
                            }}
                        >

                        </circle>


                        <text
                            className="risk-label"
                            x={0}
                            y={0}
                            dx={textDx(similarItem, similarStakeholders, props.width, baseMargin) - offsetX}
                            dy={textDy(similarItem, similarStakeholders, props.width, baseMargin) - offsetY}
                            fill={isWorse ? "#E70549" :
                                isBetter ? "#24CCB8" : "#00000077"}
                            strokeWidth={isWorse ? "#FFFFFF" :
                                isBetter ? "#FFFFFF" :
                                    "6"
                            }
                            stroke={isWorse ? "#FFFFFF55" :
                                isBetter ? "#FFFFFF55" :
                                    "#00000001"
                            }
                            style={{
                                fontSize: "12px",
                                textAnchor: Math.abs(circlePlacementDx(similarItem, similarStakeholders) * 10) < 0.1 ? "middle" :
                                    circlePlacementDx(similarItem, similarStakeholders) < 0 ? "end" :
                                        circlePlacementDx(similarItem, similarStakeholders) > 0 ? "start" : "middle",
                                alignmentBaseline: "middle",
                                transition: "fill 1.8s ease-in-out, stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out"
                            }}
                            transform='translate(0, -10)'

                        >
                            {getAllInitials(d.name)}
                            <circle
                                r={2.5}
                                fill="#000000"
                            ></circle>
                        </text>

                        <text
                            className="risk-label"
                            x={0}
                            y={0}
                            dx={textDx(similarItem, similarStakeholders, props.width, baseMargin) - offsetX}
                            dy={textDy(similarItem, similarStakeholders, props.width, baseMargin) - offsetY}
                            fill={"#000000"}
                            style={{
                                fontSize: "12px",
                                textAnchor: Math.abs(circlePlacementDx(similarItem, similarStakeholders) * 10) < 0.1 ? "middle" :
                                    circlePlacementDx(similarItem, similarStakeholders) < 0 ? "end" :
                                        circlePlacementDx(similarItem, similarStakeholders) > 0 ? "start" : "middle",
                                alignmentBaseline: "middle",
                                transition: "fill 1.8s ease-in-out, stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out"
                            }}
                            transform='translate(0, -10)'

                        >
                            {getAllInitials(d.name)}
                            <circle
                                r={2.5}
                                fill="#000000"
                            ></circle>
                        </text>



                    </g>

                })
            }
        </g >





    );
};

// React.memo : la PointsCloud ne depend pas de l etat hover/drag du chart
// parent. Wrap avec un comparateur qui ignore les callbacks (qui changent
// d identite a chaque parent render) et compare uniquement les props "data".
// Sans ca, chaque hover sur un item declenche un re-render de la cloud
// entiere -> repaint de 21 stakeholders -> flicker visible.
const propsEqual = (prev: any, next: any) => {
    return (
        prev.data === next.data &&
        prev.similarItems === next.similarItems &&
        prev.selectedPoints === next.selectedPoints &&
        prev.isSelectedMode === next.isSelectedMode &&
        prev.width === next.width &&
        prev.height === next.height &&
        prev.scenario === next.scenario &&
        prev.recentCreations === next.recentCreations &&
        prev.margin === next.margin &&
        prev.x === next.x &&
        prev.y === next.y
    );
};

export const StakeholdersPointsCloud = React.memo(StakeholdersPointsCloudInner, propsEqual);
